import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/site/ui";
import { useAuth } from "@/context/AuthContext";

export function DashShell({ nav, title, children }) {
    const loc = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);

    const doLogout = () => { logout(); navigate("/login"); };

    const Sidebar = (
        <div className="flex flex-col h-full">
            <div className="p-5 border-b border-black/5"><Logo onDark={false} /></div>
            <nav className="flex-1 p-3 space-y-1">
                {nav.map((n) => {
                    const active = loc.pathname === n.to;
                    return (
                        <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`dash-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? "bg-teal-900 text-cream" : "text-teal-900/70 hover:bg-teal-900/5"}`}>
                            <n.icon className="w-4.5 h-4.5" /> {n.label}
                        </Link>
                    );
                })}
            </nav>
            <div className="p-3 border-t border-black/5">
                <div className="px-3 py-2 text-xs text-teal-900/60">{user?.name} · <span className="capitalize">{user?.role?.replace("_", " ")}</span></div>
                <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50" data-testid="dash-logout"><LogOut className="w-4 h-4" /> Logout</button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen dash-light flex">
            <aside className="hidden lg:block w-64 bg-white border-r border-black/5 fixed inset-y-0">{Sidebar}</aside>
            {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} />}
            <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}>{Sidebar}</aside>
            <div className="flex-1 lg:ml-64">
                <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-black/5 px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button className="lg:hidden text-teal-900" onClick={() => setOpen((o) => !o)}>{open ? <X /> : <Menu />}</button>
                        <h1 className="font-serif text-xl sm:text-2xl text-teal-900">{title}</h1>
                    </div>
                    <Link to="/" className="text-sm text-teal-900/60 hover:text-teal-900">View site →</Link>
                </header>
                <main className="p-4 sm:p-6">{children}</main>
            </div>
        </div>
    );
}

export function StatCard({ label, value, icon: Icon, accent = "teal", testid }) {
    const colors = { teal: "bg-teal-900/5 text-teal-900", gold: "bg-gold/15 text-gold-amber", green: "bg-growth/10 text-growth", red: "bg-red-50 text-red-600" };
    return (
        <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm" data-testid={testid}>
            <div className="flex items-center justify-between">
                <span className="text-sm text-teal-900/60">{label}</span>
                {Icon && <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[accent]}`}><Icon className="w-4.5 h-4.5" /></div>}
            </div>
            <div className="mt-3 font-mono font-bold text-2xl text-teal-900">{value}</div>
        </div>
    );
}

export function Badge({ status }) {
    const map = {
        New: "bg-blue-100 text-blue-700", Assigned: "bg-indigo-100 text-indigo-700", Contacted: "bg-amber-100 text-amber-700",
        "Requirement Discussion": "bg-purple-100 text-purple-700", "Proposal Sent": "bg-cyan-100 text-cyan-700",
        "Payment Pending": "bg-orange-100 text-orange-700", Paid: "bg-green-100 text-green-700", "In Progress": "bg-teal-100 text-teal-700",
        "On Hold": "bg-gray-200 text-gray-700", Completed: "bg-emerald-100 text-emerald-700", Cancelled: "bg-red-100 text-red-700",
        Pending: "bg-orange-100 text-orange-700", Failed: "bg-red-100 text-red-700", "Not Applicable": "bg-gray-100 text-gray-500",
    };
    return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
}
